import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ImagePreview } from "@/hooks/useServiceForm";
import { Service } from "@/types/dashboard";
import { optimizeImage, verifyImage, generateImagePath } from "@/utils/imageUtils";

interface ServiceFormSubmitProps {
  name: string;
  description: string;
  price: number;
  discountedPrice: string;
  categoryId: string | null;
  serviceDuration: number;
  durationUnit: string;
  serviceMode: string;
  serviceLocation: string;
  instantBooking: boolean;
  requiresScheduling: boolean;
  images: ImagePreview[];
  coverImageIndex: number;
  initialData?: Service;
  travelFee: string;
  deletedImageIds?: string[];
}

interface UploadResult {
  index: number;
  url: string;
  isCoverImage: boolean;
  success: boolean;
  error?: any;
}

export const useServiceFormSubmit = (onSuccessCallback?: () => Promise<void>) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  /**
   * Process and upload images in batch
   */
  const processAndUploadImages = async (
    images: ImagePreview[],
    serviceId: string,
    userId: string,
    coverImageIndex: number
  ): Promise<{
    successfulUploads: UploadResult[];
    failedUploads: UploadResult[];
    coverImageUrl: string | null;
  }> => {
    console.log(`Processing ${images.length} images for service ${serviceId}`);
    const successfulUploads: UploadResult[] = [];
    const failedUploads: UploadResult[] = [];
    
    // Step 1: Separate existing images from new ones
    const existingImages = images.filter(img => img.isExisting);
    const newImages = images.filter(img => img.file && !img.isExisting);
    
    console.log(`Found ${existingImages.length} existing images and ${newImages.length} new images to upload`);
    
    // Add existing images to successful uploads first (we're keeping them)
    existingImages.forEach((img, idx) => {
      successfulUploads.push({
        index: idx,
        url: img.previewUrl,
        isCoverImage: idx === coverImageIndex && newImages.length === 0, // Only consider this a cover if there are no new images or it's explicitly selected
        success: true
      });
    });
    
    if (newImages.length === 0) {
      // If there are no new images, just use the existing ones
      const coverImage = coverImageIndex < existingImages.length ? existingImages[coverImageIndex] : null;
      return {
        successfulUploads,
        failedUploads,
        coverImageUrl: coverImage ? coverImage.previewUrl : null
      };
    }
    
    // Step 2: Process new images (optimize and verify) in parallel
    const processedImages = await Promise.all(
      newImages.map(async (image, idx) => {
        try {
          if (!image.file) return null;
          
          // Verify the image
          await verifyImage(image.file);
          
          // Optimize the image
          const optimizedFile = await optimizeImage(image.file);
          
          // Generate a unique path including the service ID for better organization
          const imagePath = generateImagePath(userId, serviceId, optimizedFile.name);
          
          // Calculate the true index for determining cover image (after existing images)
          const actualIndex = existingImages.length + idx;
          
          return {
            index: actualIndex,
            file: optimizedFile,
            path: imagePath,
            isCoverImage: actualIndex === coverImageIndex
          };
        } catch (error) {
          console.error(`Error processing image ${idx}:`, error);
          failedUploads.push({ 
            index: existingImages.length + idx, 
            url: '', 
            isCoverImage: (existingImages.length + idx) === coverImageIndex,
            success: false, 
            error 
          });
          return null;
        }
      })
    );
    
    // Filter out null values from failed preparations
    const imagesToUpload = processedImages.filter(img => img !== null) as {
      index: number;
      file: File;
      path: string;
      isCoverImage: boolean;
    }[];
    
    console.log(`Prepared ${imagesToUpload.length} new images for upload`);
    
    // Step 3: Upload images in batches of 3 for better control and progress tracking
    const BATCH_SIZE = 3;
    const batches = [];
    
    for (let i = 0; i < imagesToUpload.length; i += BATCH_SIZE) {
      batches.push(imagesToUpload.slice(i, i + BATCH_SIZE));
    }
    
    console.log(`Split uploads into ${batches.length} batches`);
    
    let completedUploads = 0;
    
    for (const [batchIndex, batch] of batches.entries()) {
      console.log(`Processing batch ${batchIndex + 1} of ${batches.length}`);
      
      // Process each batch in parallel
      const batchResults = await Promise.all(
        batch.map(async (preparedImage) => {
          try {
            const { error: uploadError } = await supabase.storage
              .from('service_images')
              .upload(preparedImage.path, preparedImage.file, {
                cacheControl: '3600',
                upsert: true, // Use upsert to handle any duplicates
                contentType: preparedImage.file.type
              });
              
            if (uploadError) throw uploadError;
            
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const imageUrl = `${supabaseUrl}/storage/v1/object/public/service_images/${preparedImage.path}?t=${Date.now()}`;
            
            completedUploads++;
            setUploadProgress(Math.round((completedUploads / imagesToUpload.length) * 100));
            
            const result = {
              index: preparedImage.index,
              url: imageUrl,
              isCoverImage: preparedImage.isCoverImage,
              success: true
            };
            
            successfulUploads.push(result);
            return result;
          } catch (error) {
            console.error(`Error uploading image ${preparedImage.index}:`, error);
            const result = {
              index: preparedImage.index,
              url: '',
              isCoverImage: preparedImage.isCoverImage,
              success: false,
              error
            };
            failedUploads.push(result);
            return result;
          }
        })
      );
      
      console.log(`Batch ${batchIndex + 1} results:`, {
        success: batchResults.filter(r => r.success).length,
        failed: batchResults.filter(r => !r.success).length
      });
    }
    
    // Find the coverImageUrl from all successful uploads (both existing and new)
    const coverImageUrl = successfulUploads.find(img => img.isCoverImage)?.url || null;
    
    return {
      successfulUploads,
      failedUploads,
      coverImageUrl
    };
  };
  
  /**
   * Display appropriate toast messages based on upload results
   */
  const handleImageUploadResults = (results: {
    successfulUploads: UploadResult[];
    failedUploads: UploadResult[];
  }) => {
    const { successfulUploads, failedUploads } = results;
    
    if (failedUploads.length === 0) {
      if (successfulUploads.length > 0) {
        toast({
          title: "Images Uploaded",
          description: `All ${successfulUploads.length} images were uploaded successfully.`,
        });
      }
      return;
    }
    
    if (successfulUploads.length === 0) {
      toast({
        title: "Image Upload Failed",
        description: "All images failed to upload. Please try again.",
        variant: "destructive",
      });
      return;
    }
    
    // Some succeeded, some failed
    toast({
      title: "Partial Upload Success",
      description: `${successfulUploads.length} images uploaded, ${failedUploads.length} failed.`,
      variant: "default"
    });
  };

  const submitService = async ({
    name,
    description,
    price,
    discountedPrice,
    categoryId,
    serviceDuration,
    durationUnit,
    serviceMode,
    serviceLocation,
    instantBooking,
    requiresScheduling,
    images,
    coverImageIndex,
    initialData,
    travelFee,
    deletedImageIds = []
  }: ServiceFormSubmitProps): Promise<boolean> => {
    if (!name || !price) {
      toast({
        title: "Missing Information",
        description: "Please provide a name and price for your service.",
        variant: "destructive",
      });
      return false;
    }

    // Validate that discounted price is not higher than or equal to regular price
    if (discountedPrice && discountedPrice.trim() !== '') {
      const discountedValue = parseFloat(discountedPrice);
      if (!isNaN(discountedValue) && discountedValue >= price) {
        toast({
          title: "Invalid Discount",
          description: "Discounted price must be less than the regular price.",
          variant: "destructive",
        });
        return false;
      }
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      console.log("Preparing service data for submission");
      
      // Calculate discounted price if provided
      const discountedPriceNum = discountedPrice ? parseFloat(discountedPrice) : null;
      
      // Calculate travel fee if provided
      const travelFeeNum = travelFee && serviceMode === "client-location" ? parseFloat(travelFee) : null;

      // Prepare service data
      const serviceData = {
        name,
        description: description || null,
        price,
        discounted_price: discountedPriceNum,
        category_id: categoryId,
        duration: serviceDuration,
        duration_unit: durationUnit,
        service_mode: serviceMode,
        service_area: serviceLocation,
        is_active: true,
        instant_booking: instantBooking,
        requires_scheduling: requiresScheduling,
        travel_fee: travelFeeNum
      };

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('No authenticated user found');
      }
      
      let serviceId: string;
      let isNewService = !initialData;

      if (initialData) {
        // Update existing service
        console.log("Updating existing service with ID:", initialData.id);
        serviceId = initialData.id;

        const { data, error } = await supabase
          .from('services')
          .update(serviceData)
          .eq('id', initialData.id)
          .select()
          .single();

        if (error) {
          console.error("Error updating service:", error);
          throw error;
        }

        console.log("Service updated successfully:", data);
        
        // Handle images marked for deletion
        if (deletedImageIds.length > 0) {
          console.log(`Processing deletion of ${deletedImageIds.length} images:`, deletedImageIds);
          
          // First get the image URLs of images to be deleted so we can remove them from storage
          const { data: imagesToRemove, error: fetchError } = await supabase
            .from('service_images')
            .select('id, image_url')
            .in('id', deletedImageIds);
            
          if (fetchError) {
            console.error("Error fetching images to remove:", fetchError);
          } else {
            const foundImageIds = imagesToRemove?.map(img => img.id) || [];
            const remainingImageIds = deletedImageIds.filter(id => foundImageIds.includes(id));
            
            console.log(`Found ${imagesToRemove?.length || 0} images to remove from database (out of ${deletedImageIds.length} marked for deletion)`);
            
            if (remainingImageIds.length === 0) {
              console.log("All images have already been deleted, skipping deletion step");
            } else if (remainingImageIds.length < deletedImageIds.length) {
              console.log(`Some images (${deletedImageIds.length - remainingImageIds.length}) were already deleted, proceeding with ${remainingImageIds.length} remaining`);
              
              // Delete the database records for remaining images
              const { error: deleteError, count } = await supabase
                .from('service_images')
                .delete()
                .in('id', remainingImageIds)
                .select('count');
                
              if (deleteError) {
                console.error("Error deleting image records:", deleteError);
                // Continue with the rest of the process, don't throw
              } else {
                console.log(`Successfully deleted ${count || 0} image records from database`);
                
                // Now remove the files from storage
                if (imagesToRemove && imagesToRemove.length > 0) {
                  const storagePathsToDelete = imagesToRemove.map(img => {
                    // Extract just the path part from the full URL
                    const path = img.image_url.replace(`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/service_images/`, '');
                    console.log(`Extracted path for image ${img.id}: ${path}`);
                    return path;
                  }).filter(path => path.includes('/'));  // Ensure it's a valid path with a slash
                  
                  if (storagePathsToDelete.length > 0) {
                    console.log(`Removing ${storagePathsToDelete.length} files from storage:`, storagePathsToDelete);
                    
                    const { error: storageError, data: storageData } = await supabase.storage
                      .from('service_images')
                      .remove(storagePathsToDelete);
                      
                    if (storageError) {
                      console.error("Error removing image files from storage:", storageError);
                      // Continue with the process, don't throw
                    } else {
                      console.log("Successfully removed image files from storage:", storageData);
                    }
                  } else {
                    console.log("No valid storage paths to delete");
                  }
                }
              }
            } else {
              // All images found, proceed with normal deletion
              // Delete the database records
              const { error: deleteError, count } = await supabase
                .from('service_images')
                .delete()
                .in('id', deletedImageIds)
                .select('count');
                
              if (deleteError) {
                console.error("Error deleting image records:", deleteError);
                // Continue with the rest of the process, don't throw
              } else {
                console.log(`Successfully deleted ${count || 0} image records from database`);
                
                // Now remove the files from storage
                if (imagesToRemove && imagesToRemove.length > 0) {
                  const storagePathsToDelete = imagesToRemove.map(img => {
                    // Extract just the path part from the full URL
                    const path = img.image_url.replace(`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/service_images/`, '');
                    console.log(`Extracted path for image ${img.id}: ${path}`);
                    return path;
                  }).filter(path => path.includes('/'));  // Ensure it's a valid path with a slash
                  
                  if (storagePathsToDelete.length > 0) {
                    console.log(`Removing ${storagePathsToDelete.length} files from storage:`, storagePathsToDelete);
                    
                    const { error: storageError, data: storageData } = await supabase.storage
                      .from('service_images')
                      .remove(storagePathsToDelete);
                      
                    if (storageError) {
                      console.error("Error removing image files from storage:", storageError);
                      // Continue with the process, don't throw
                    } else {
                      console.log("Successfully removed image files from storage:", storageData);
                    }
                  } else {
                    console.log("No valid storage paths to delete");
                  }
                }
              }
            }
          }
        }
      } else {
        // Create a new service
        console.log("Creating new service");

        const { data: newService, error } = await supabase
          .from('services')
          .insert([
            {
              ...serviceData,
              user_id: user.id,
            },
          ])
          .select()
          .single();

        if (error) {
          console.error("Error creating service:", error);
          throw error;
        }

        console.log("Service created successfully:", newService);
        serviceId = newService.id;
      }

      // Process and upload all images
      if (images && images.length > 0) {
        console.log(`Processing ${images.length} images`);
        
        toast({
          title: "Uploading Images",
          description: "Processing and uploading your images...",
        });
        
        const uploadResults = await processAndUploadImages(
          images,
          serviceId,
          user.id,
          coverImageIndex
        );
        
        const { successfulUploads, failedUploads, coverImageUrl } = uploadResults;
        
        console.log("Image upload results:", {
          successful: successfulUploads.length,
          failed: failedUploads.length,
          coverImage: coverImageUrl
        });
        
        // Display upload results to the user
        handleImageUploadResults(uploadResults);
        
        // Update the service with the cover image URL
        if (coverImageUrl) {
          console.log("Updating service with cover image:", coverImageUrl);
          
          const { error: updateError } = await supabase
            .from('services')
            .update({ image_url: coverImageUrl })
            .eq('id', serviceId);

          if (updateError) {
            console.error("Error updating service with cover image URL:", updateError);
            // We don't throw here, we'll continue with the rest of the process
          } else {
            console.log("Service updated with cover image URL successfully");
          }
        }
        
        // If we're updating an existing service, first get existing service_images records
        let existingImageRecords: any[] = [];
        if (initialData) {
          const { data: existingImages } = await supabase
            .from('service_images')
            .select('id, image_url, sequence')
            .eq('service_id', serviceId);
            
          existingImageRecords = existingImages || [];
          console.log(`Found ${existingImageRecords.length} existing image records in database`);
        }
        
        // Filter for just the new image uploads (not existing ones)
        const newImageUploads = successfulUploads.filter(img => {
          // Check if this URL doesn't exist in the existing records
          return !existingImageRecords.some(record => record.image_url === img.url);
        });
        
        // Save non-cover images to the service_images table (only new ones)
        const additionalImages = newImageUploads.filter(img => !img.isCoverImage);
        
        if (additionalImages.length > 0) {
          console.log(`Saving ${additionalImages.length} additional images to database`);
          
          // Use the highest existing sequence number as the starting point
          let startSequence = 0;
          if (existingImageRecords.length > 0) {
            const maxSequence = Math.max(...existingImageRecords.map(r => r.sequence || 0));
            startSequence = maxSequence + 1;
          }
          
          const imageRecords = additionalImages.map((img, idx) => ({
            service_id: serviceId,
            image_url: img.url,
            sequence: startSequence + idx
          }));
          
          const { error: imageRecordError } = await supabase
            .from('service_images')
            .insert(imageRecords);

          if (imageRecordError) {
            console.error("Error creating service_image records:", imageRecordError);
            // We don't throw here, we'll continue with the rest of the process
          } else {
            console.log("Service_image records created successfully");
          }
        }
      }

      toast({
        title: "Service Saved",
        description: "Your service has been saved successfully.",
      });

      if (onSuccessCallback) {
        await onSuccessCallback();
      }
      
      return true;
    } catch (error) {
      console.error("Error submitting service:", error);
      toast({
        title: "Error Saving Service",
        description: "There was a problem saving your service. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return {
    submitService,
    isSubmitting,
    uploadProgress
  };
};
